/**
 * R268y · Feb 2026 — Finance Hub landing page.
 *
 * Purpose: single "home" for every money-related page. Replaces the
 * flat 13-item sidebar with 4-5 grouped shortcut cards + live KPI
 * strip pulled from `/revenue-flow`.
 *
 * The old `/app/finance` route (bookkeeping — accounts, journal
 * entries, expenses) still exists, moved under **Bookkeeping** in
 * the hub grid.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { fmtMoney } from "@/lib/currency";
import {
  Receipt, TrendingUp, HandCoins, Truck, PieChart, Bell,
  ShieldCheck, Lock, Wallet, BookOpen, Repeat, Building2,
  FileText, Bot, Sparkles, Server,
} from "lucide-react";

const HUB_SECTIONS = [
  {
    key: "sales", title: "Sales · AR", subtitle: "Money owed to you",
    color: "emerald",
    items: [
      { to: "/app/sales",                         label: "Quotes → Invoices", icon: Receipt,      desc: "Estimate, sales order, invoice, credit note, payment receipt.", perm: "finance.sales.quotes.view" },
      { to: "/app/finance/receivables",           label: "Receivables (AR)",  icon: HandCoins,    desc: "Aged AR, collection playbook, dunning-safe follow-ups.",         perm: "finance.sales.receivables.view" },
      { to: "/app/finance/dunning",               label: "Dunning Queue",     icon: Bell,         desc: "Automated overdue-invoice reminders.",                            perm: "finance.sales.dunning.view" },
    ],
  },
  {
    key: "purchases", title: "Purchases · AP", subtitle: "Money you owe",
    color: "amber",
    items: [
      { to: "/app/finance/vendors",               label: "Vendors & Bills",   icon: Truck,        desc: "Vendor master, purchase orders, bills, RCM.",                perm: "finance.purchases.vendors.view" },
      { to: "/app/finance/pay-run",               label: "Payroll & Pay-run", icon: Wallet,       desc: "Consolidated employee + vendor payouts.",                    perm: "finance.purchases.payrun.view" },
      { to: "/app/finance",                       label: "Expenses & Ledger", icon: BookOpen,     desc: "Journal entries, expense capture, chart of accounts.",       perm: "finance.purchases.expenses.view" },
    ],
  },
  {
    key: "reports", title: "Reports · Insight", subtitle: "See where the money is going",
    color: "blue",
    items: [
      { to: "/app/finance/revenue-flow",          label: "Revenue Flow",      icon: TrendingUp,   desc: "Pipeline → invoiced → collected · aged AR · cashflow.",   perm: "finance.reports.revenue_flow.view" },
      { to: "/app/finance/reports",               label: "Financial Reports", icon: PieChart,     desc: "P&L, balance sheet, trial balance, cash-flow.",           perm: "finance.reports.financial.view" },
      { to: "/app/finance/leadership",            label: "Leadership View",   icon: FileText,     desc: "CFO board-pack: run-rate, burn, margin, deals-per-rep.", perm: "finance.reports.leadership.view" },
      { to: "/app/finance/copilot",               label: "AI Copilot",        icon: Bot,          desc: "Ask your books natural-language questions.",              perm: "finance.reports.copilot.view" },
      { to: "/app/finance/scenario-planner",      label: "Scenario Planner",  icon: Sparkles,     desc: "What-if forecasting on budgets, spend and hiring.",        perm: "finance.reports.scenario.view" },
    ],
  },
  {
    key: "compliance", title: "Compliance", subtitle: "GST · TDS · Period-close",
    color: "violet",
    items: [
      { to: "/app/finance/msme",                  label: "MSME · GST",        icon: Repeat,       desc: "MSME payables ageing, GSTR-1/3B roll-up, TDS.",           perm: "finance.compliance.msme.view" },
      { to: "/app/finance/close",                 label: "Period Close",      icon: Lock,         desc: "Month-end checklist and audit trail.",                    perm: "finance.compliance.close.view" },
      { to: "/app/finance/advanced",              label: "Advanced (Audit)",  icon: Server,       desc: "Ledger export, opening-balance import, backfills.",       perm: "finance.compliance.advanced.view" },
      { to: "/app/settings/billing-profile",      label: "Org Billing Profile", icon: Building2,  desc: "Legal entity, GSTIN, bank, PDF template.",                perm: "finance.compliance.billing_profile.view" },
    ],
  },
];

const _COLOR = {
  emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100",
  amber:   "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100",
  blue:    "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100",
  violet:  "bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-800 text-violet-900 dark:text-violet-100",
};

export default function FinanceHub() {
  const { activeWorkspaceId } = useAuth();
  const { can } = useWorkspacePerms();
  const wsId = activeWorkspaceId;
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [profile, setProfile] = useState(null);

  // Feb 2026 · Filter Hub cards by effective permissions. Anyone who
  // holds the legacy `finance.view` / `finance.manage` (or the workspace
  // admin bypass) still sees every card. Custom "Accounts" roles get
  // only the sub-screens they were granted.
  const _canScreen = (perm) => {
    if (!perm) return true;  // untagged card — always visible
    return can(perm) || can("finance.view") || can("finance.manage") || can("workspace.admin");
  };
  const visibleSections = useMemo(() => (
    HUB_SECTIONS
      .map((sec) => ({ ...sec, items: sec.items.filter((it) => _canScreen(it.perm)) }))
      .filter((sec) => sec.items.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [can]);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    (async () => {
      try {
        const [rf, bp] = await Promise.all([
          api.get(`/workspaces/${wsId}/revenue-flow?months=6`),
          api.get(`/gst/workspaces/${wsId}/billing-profile`).catch(() => ({ data: null })),
        ]);
        if (!cancelled) {
          setKpis(rf.data);
          setProfile(bp?.data || null);
        }
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [wsId]);

  const home = profile?.home_currency || "INR";
  const kpiTiles = useMemo(() => {
    if (!kpis) return [];
    const f = kpis.funnel || {};
    const s = kpis.stage_counts || {};
    return [
      { label: "Invoiced (6m)",    value: fmtMoney(f.invoiced_value || 0, home), sub: `${s.invoices_open ?? 0} open · ${s.invoices_paid ?? 0} paid`, color: "emerald" },
      { label: "Collected (6m)",   value: fmtMoney(f.collected_value || 0, home), sub: "cash received", color: "blue" },
      { label: "Outstanding AR",   value: fmtMoney(f.outstanding_value || 0, home), sub: "waiting on customers", color: "amber" },
      { label: "Pipeline value",   value: fmtMoney(f.pipeline_value || 0, home), sub: `${s.leads_open ?? 0} open leads`, color: "violet" },
    ];
  }, [kpis, home]);

  return (
    <>
      <PageHeader
        title="Finance"
        description="Every money view — sales, AR, AP, reporting, compliance — grouped here."
        eyebrow="Money"
        data-testid="finance-hub"
      />
      <div className="px-4 sm:px-6 md:px-10 space-y-6 max-w-7xl">
        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finance-hub-kpis">
          {kpiTiles.map((k) => (
            <div key={k.label}
                    className={"border p-4 space-y-1 " + _COLOR[k.color]}
                    data-testid={`finance-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
              <div className="text-[11px] uppercase tracking-widest opacity-70">{k.label}</div>
              <div className="text-2xl font-bold tabular-nums">{k.value}</div>
              <div className="text-xs opacity-70">{k.sub}</div>
            </div>
          ))}
          {!kpis && (
            <div className="col-span-full text-xs text-muted-foreground">Loading KPIs…</div>
          )}
        </div>

        {/* Grouped shortcuts */}
        {visibleSections.length === 0 && (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="finance-hub-no-access">
            No Finance screens have been unlocked for your role. Ask a workspace admin to grant you Finance permissions on <code>/app/roles</code>.
          </div>
        )}
        {visibleSections.map((sec) => (
          <section key={sec.key} data-testid={`finance-section-${sec.key}`} className="space-y-2">
            <div className="flex items-baseline gap-3">
              <h2 className="text-lg font-bold">{sec.title}</h2>
              <span className="text-xs text-muted-foreground">{sec.subtitle}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sec.items.map((it) => {
                const Ico = it.icon;
                return (
                  <button
                    key={it.to}
                    onClick={() => navigate(it.to)}
                    data-testid={`finance-card-${it.to.split("/").pop()}`}
                    className="text-left border border-border rounded-lg p-4 space-y-1 hover:border-primary/50 hover:bg-muted/40 transition"
                  >
                    <div className="flex items-center gap-2">
                      <Ico className="h-4 w-4 text-primary" />
                      <span className="font-semibold">{it.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{it.desc}</div>
                  </button>
                );
              })}
            </div>
          </section>
        ))}

        <div className="text-[11px] text-muted-foreground pt-4">
          Missing something? All money screens live inside this hub — nothing else has to be in the sidebar.
        </div>
      </div>
    </>
  );
}
